// src/components/admin/AdminRevenue.jsx
// Subscription & Revenue Analytics — Phase 2 (mobile-responsive)

import { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';

// ── Helpers ───────────────────────────────────────────────────
const fmt$ = (n, decimals = 0) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80, color: 'var(--gray)', fontSize: 13 }}>
      Loading revenue data…
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────
function KPICard({ label, value, sub, color = 'var(--gold)', badge, trend }) {
  return (
    <div style={{
      background: 'var(--dark2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', padding: '16px 18px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${color}, transparent 70%)` }}/>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--gray)' }}>{label}</span>
        {badge && (
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.06em', color, background: `${color}18`, border: `1px solid ${color}30`, padding: '2px 7px', borderRadius: 3 }}>
            {badge}
          </span>
        )}
      </div>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, color, lineHeight: 1, letterSpacing: '.02em' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 5 }}>{sub}</div>}
      {trend !== undefined && (
        <div style={{ marginTop: 7, display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: trend >= 0 ? 'var(--green)' : 'var(--red)', background: trend >= 0 ? 'rgba(46,204,138,.12)' : 'rgba(217,79,79,.12)', padding: '2px 7px', borderRadius: 3 }}>
            {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
          <span style={{ fontSize: 10, color: 'var(--gray)' }}>vs last month</span>
        </div>
      )}
    </div>
  );
}

// ── Bar Chart ─────────────────────────────────────────────────
function BarChart({ data, color = 'var(--gold)', height = 120, prefix = '', formatVal }) {
  const values = Object.values(data);
  const labels = Object.keys(data);
  const max    = Math.max(...values) || 1;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height, paddingBottom: 20, position: 'relative' }}>
      {values.map((v, i) => {
        const barH  = max > 0 ? ((v / max) * (height - 24)) : 0;
        const isLast = i === values.length - 1;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, height: '100%', justifyContent: 'flex-end' }}>
            {v > 0 && (
              <span style={{ fontSize: 8, color: isLast ? color : 'var(--gray)', fontWeight: isLast ? 700 : 400, whiteSpace: 'nowrap' }}>
                {formatVal ? formatVal(v) : `${prefix}${v}`}
              </span>
            )}
            <div style={{ width: '100%', height: barH, background: isLast ? color : `${color}55`, borderRadius: '3px 3px 0 0', minHeight: v > 0 ? 3 : 0, transition: 'height .4s ease' }}/>
            <span style={{ fontSize: 7.5, color: 'rgba(247,243,234,.3)', transform: 'rotate(-35deg)', transformOrigin: 'top center', whiteSpace: 'nowrap', marginTop: 2 }}>{labels[i]}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Line Chart (SVG) ─────────────────────────────────────────
function LineChart({ data, color = 'var(--gold)', height = 100 }) {
  const values = Object.values(data);
  const labels = Object.keys(data);
  if (values.length < 2) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray)', fontSize: 12 }}>
      Not enough data
    </div>
  );

  const W = 500, H = height;
  const min = 0, max = Math.max(...values) || 1;
  const pts = values.map((v, i) => [
    (i / (values.length - 1)) * W,
    H - 20 - ((v - min) / (max - min)) * (H - 30),
  ]);
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const fill = `${path} L${W},${H} L0,${H} Z`;

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height, display: 'block' }}>
        <defs>
          <linearGradient id="lgrev" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity=".2"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={fill} fill="url(#lgrev)"/>
        <path d={path} fill="none" stroke={color} strokeWidth="2"/>
        {pts.map((p, i) => (
          <circle key={i} cx={p[0]} cy={p[1]} r="3" fill={i === pts.length - 1 ? color : 'var(--dark2)'} stroke={color} strokeWidth="1.5"/>
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        {labels.filter((_, i) => i % Math.ceil(labels.length / 6) === 0 || i === labels.length - 1).map(l => (
          <span key={l} style={{ fontSize: 9, color: 'rgba(247,243,234,.35)' }}>{l}</span>
        ))}
      </div>
    </div>
  );
}

// ── Plan Donut ────────────────────────────────────────────────
function PlanDonut({ plans }) {
  if (!plans?.length) return <div style={{ color: 'var(--gray)', fontSize: 12 }}>No active plans</div>;

  const total  = plans.reduce((s, p) => s + p.count, 0) || 1;
  const COLORS = ['var(--gold)', 'var(--blue)', 'var(--green)', '#f472b6', '#a78bfa'];
  const R = 52, cx = 70, cy = 70, stroke = 18;
  const circ = 2 * Math.PI * R;

  let offset = 0;
  const segments = plans.map((p, i) => {
    const pct  = p.count / total;
    const dash = pct * circ;
    const seg  = { ...p, dash, offset, color: COLORS[i % COLORS.length] };
    offset += dash;
    return seg;
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <svg width={140} height={140} viewBox="0 0 140 140" style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="rgba(255,255,255,.06)" strokeWidth={stroke}/>
        {segments.map((s, i) => (
          <circle key={i} cx={cx} cy={cy} r={R} fill="none"
            stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${s.dash} ${circ - s.dash}`}
            strokeDashoffset={-s.offset + circ / 4}
            style={{ transition: 'stroke-dasharray .5s ease' }}
          />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="18" fontWeight="700" fill="var(--white)" fontFamily="'Bebas Neue',sans-serif">{total}</text>
        <text x={cx} y={cy + 10} textAnchor="middle" fontSize="8" fill="var(--gray)" fontFamily="'Inter',sans-serif" letterSpacing=".06em">ACTIVE</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 120 }}>
        {segments.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }}/>
            <span style={{ fontSize: 12, color: 'var(--off-white)', flex: 1 }}>{s.name}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: s.color }}>{s.count}</span>
            <span style={{ fontSize: 11, color: 'var(--gray)', width: 40, textAlign: 'right' }}>{fmt$(s.revenue)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Responsive styles injected once ──────────────────────────
const MOBILE_CSS = `
@media (max-width: 640px) {
  .rev-kpi-grid { grid-template-columns: repeat(2, 1fr) !important; }
  .rev-bottom-row { grid-template-columns: 1fr !important; }
  .rev-sub-churn-row { grid-template-columns: 1fr !important; }
  .rev-tab-btn { padding: 0 8px !important; font-size: 10px !important; }
}
`;

function InjectStyle({ id, css }) {
  useEffect(() => {
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = css;
    document.head.appendChild(el);
  }, []);
  return null;
}

// ── Main Component ────────────────────────────────────────────
export default function AdminRevenue() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [view,    setView]    = useState('subs'); // subs | revenue | churn

  useEffect(() => {
    adminApi.getRevenue()
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner/>;
  if (error)   return <div style={{ padding: 40, color: 'var(--red)', fontSize: 13 }}>Failed to load revenue data: {error}</div>;

  const subVals   = Object.values(data.newSubsByMonth);
  const momGrowth = subVals.length >= 2 && subVals[subVals.length - 2] > 0
    ? Math.round(((subVals[subVals.length - 1] - subVals[subVals.length - 2]) / subVals[subVals.length - 2]) * 100)
    : null;

  const revVals   = Object.values(data.revenueByMonth);
  const revGrowth = revVals.length >= 2 && revVals[revVals.length - 2] > 0
    ? Math.round(((revVals[revVals.length - 1] - revVals[revVals.length - 2]) / revVals[revVals.length - 2]) * 100)
    : null;

  const chartData  = view === 'subs' ? data.newSubsByMonth : view === 'revenue' ? data.revenueByMonth : data.churnByMonth;
  const chartColor = view === 'revenue' ? 'var(--green)' : view === 'churn' ? 'var(--red)' : 'var(--gold)';

  return (
    <>
      <InjectStyle id="rev-mobile-css" css={MOBILE_CSS}/>
      <div style={{ padding: '14px 14px 60px', animation: 'pageIn .3s ease', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* KPI Row */}
        <div className="rev-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          <KPICard label="MRR"           value={fmt$(data.MRR, 2)}       color="var(--gold)"  badge="MONTHLY"  sub="Monthly Recurring Revenue"   trend={revGrowth}/>
          <KPICard label="ARR"           value={fmt$(data.ARR)}          color="var(--blue)"  badge="ANNUAL"   sub="Annualised Recurring Revenue"/>
          <KPICard label="Active Subs"   value={data.activeSubsCount}    color="var(--green)" badge="LIVE"     sub="Currently paying"            trend={momGrowth}/>
          <KPICard label="Churn Rate"    value={`${data.churnRate}%`}    color={data.churnRate > 5 ? 'var(--red)' : 'var(--green)'} badge="MoM" sub="Cancellations this month"/>
          <KPICard label="ARPU"          value={fmt$(data.ARPU, 2)}      color="var(--gold)"  badge="PER USER" sub="Avg revenue per user"/>
          <KPICard label="Total Revenue" value={fmt$(data.totalRevenue)} color="var(--green)" badge="ALL TIME" sub="Lifetime revenue"/>
        </div>

        {/* Main chart panel */}
        <div className="panel">
          <div className="panel-hd" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div className="panel-title" style={{ fontSize: 13 }}><b>Subscription</b> Trends · Last 12 Months</div>
            <div style={{ display: 'flex', gap: 0, marginLeft: 'auto' }}>
              {[['subs','New Subs'],['revenue','Revenue'],['churn','Churn']].map(([v, l]) => (
                <button
                  key={v} onClick={() => setView(v)}
                  className="rev-tab-btn"
                  style={{ padding: '0 12px', height: 30, background: view === v ? 'rgba(212,168,67,.1)' : 'transparent', border: 'none', borderRight: '1px solid rgba(255,255,255,.04)', color: view === v ? 'var(--gold)' : 'rgba(247,243,234,.45)', fontSize: 11, fontWeight: view === v ? 700 : 500, cursor: 'pointer', fontFamily: "'Inter',sans-serif", letterSpacing: '.03em' }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <LineChart data={chartData} color={chartColor} height={140}/>
        </div>

        {/* Plan distribution + Monthly revenue */}
        <div className="rev-bottom-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="panel">
            <div className="panel-hd">
              <div className="panel-title"><b>Plan</b> Distribution</div>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>Active subscribers</span>
            </div>
            <PlanDonut plans={data.planDistribution}/>
          </div>
          <div className="panel">
            <div className="panel-hd">
              <div className="panel-title"><b>Monthly</b> Revenue</div>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>Last 12 months</span>
            </div>
            <BarChart
              data={data.revenueByMonth}
              color="var(--green)"
              height={160}
              formatVal={v => v >= 1000 ? `$${(v/1000).toFixed(1)}k` : `$${v}`}
            />
          </div>
        </div>

        {/* New subs + churn */}
        <div className="rev-sub-churn-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="panel">
            <div className="panel-hd">
              <div className="panel-title"><b>New</b> Subscriptions</div>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>Per month</span>
            </div>
            <BarChart data={data.newSubsByMonth} color="var(--gold)" height={130}/>
          </div>
          <div className="panel">
            <div className="panel-hd">
              <div className="panel-title"><b>Churn</b></div>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>Cancellations / month</span>
            </div>
            <BarChart data={data.churnByMonth} color="var(--red)" height={130}/>
            {Object.values(data.churnByMonth).every(v => v === 0) && (
              <div style={{ textAlign: 'center', color: 'var(--green)', fontSize: 12, marginTop: -80, paddingBottom: 20 }}>✓ No churn recorded</div>
            )}
          </div>
        </div>

      </div>
    </>
  );
}